import React from 'react';
import { OmniIconComponent, OmniLoadingIndicatorComponent } from './omni-components';
import { MediaOpsService } from '../services/mediaops-service';
import '../../styles/globalSearch.scss';

export class GlobalSearch extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            searchQuery: '',
            results: {
                campaigns: [],
                placements: [],
                creatives: []
            },
            isLoading: false,
            selectedIndex: -1,
            totalResults: 0
        };
        this.searchInputRef = React.createRef();
        this.debounceTimeout = null;
    }

    componentDidMount() {
        // Auto-focus search input when opened
        if (this.searchInputRef.current) {
            this.searchInputRef.current.focus();
        }
        
        // Add keyboard event listener
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        // Clean up event listener
        document.removeEventListener('keydown', this.handleKeyDown);
        
        // Clear any pending debounce
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
    }

    /**
     * Handles keyboard navigation within the search overlay.
     * - Escape: closes the overlay
     * - ArrowDown/ArrowUp: moves the highlighted result selection
     * - Enter: activates the currently highlighted result
     * @param {KeyboardEvent} e
     */
    handleKeyDown = (e) => {
        const { selectedIndex } = this.state;
        const { onClose } = this.props;

        // Escape to close
        if (e.key === 'Escape') {
            onClose();
            return;
        }

        // Arrow Down - Move selection down
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState(prevState => ({
                selectedIndex: prevState.selectedIndex < prevState.totalResults - 1 
                    ? prevState.selectedIndex + 1 
                    : prevState.selectedIndex
            }));
            return;
        }

        // Arrow Up - Move selection up
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState(prevState => ({
                selectedIndex: prevState.selectedIndex > 0 
                    ? prevState.selectedIndex - 1 
                    : -1
            }));
            return;
        }

        // Enter - Select highlighted result
        if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            this.handleResultSelect();
        }
    };

    /**
     * Handles changes to the search input.
     * Debounces the API call by 300ms and sets loading state immediately
     * when the query is at least 2 characters long.
     * @param {React.ChangeEvent<HTMLInputElement>} e
     */
    handleSearchChange = (e) => {
        const searchQuery = e.target.value;
        this.setState({ searchQuery, selectedIndex: -1 });

        // Clear previous timeout
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }

        // Set loading state immediately if query is valid
        if (searchQuery.length >= 4) {
            this.setState({ isLoading: true });
        }

        // Debounce API call (300ms delay)
        this.debounceTimeout = setTimeout(() => {
            if (searchQuery.length >= 4) {
                this.performGlobalSearch(searchQuery);
            } else {
                this.setState({
                    results: { campaigns: [], placements: [], creatives: [] },
                    isLoading: false,
                    totalResults: 0
                });
            }
        }, 300);
    };

    /**
     * Calls the global search API and segregates the flat result array
     * into campaigns, placements, and creatives by their `item_type` field.
     * Updates component state with the segregated results and total count.
     * @param {string} searchQuery - The search term to send to the API
     */
    performGlobalSearch = async (searchQuery) => {
        try {
            const response = await MediaOpsService.globalSearch(searchQuery);
            const items = response?.Items || [];

            const campaigns = items.filter(item => item.item_type === 'campaign');
            const placements = items.filter(item => item.item_type === 'placement');
            const creatives = items.filter(item => item.item_type?.toLowerCase().includes('creative'));

            const totalResults = response.Count || 0;

            this.setState({
                results: { campaigns, placements, creatives },
                isLoading: false,
                totalResults
            });
        } catch (error) {
            console.error('Global search error:', error);
            this.setState({
                results: { campaigns: [], placements: [], creatives: [] },
                isLoading: false,
                totalResults: 0
            });
        }
    };

    /**
     * Handles a result item being clicked.
     * Closes the overlay and either calls the `onResultSelect` prop callback
     * or falls back to the default navigation logic.
     * @param {Object} result - The search result item
     * @param {'campaign'|'placement'|'creatives'} type - The entity type
     */
    handleResultClick = (result, type) => {
        const { onResultSelect, onClose, navigate } = this.props;
        
        // Close search overlay
        onClose();
        
        // Navigate based on result type
        if (onResultSelect) {
            onResultSelect(result, type);
        } else {
            // Default navigation logic
            this.navigateToResult(result, type);
        }
    };

    /**
     * Activates the currently highlighted result when Enter is pressed.
     * Resolves the selected item from the flattened results list by index.
     */
    handleResultSelect = () => {
        const { selectedIndex } = this.state;
        const allResults = this.getAllResultsFlat();
        
        if (selectedIndex >= 0 && selectedIndex < allResults.length) {
            const { result, type } = allResults[selectedIndex];
            this.handleResultClick(result, type);
        }
    };

    /**
     * Returns a flat ordered array of all result items with their type.
     * Order: campaigns → placements → creatives (matches render order
     * so keyboard selectedIndex aligns correctly).
     * @returns {{ result: Object, type: string }[]}
     */
    getAllResultsFlat = () => {
        const { results } = this.state;
        const flat = [];
        
        results.campaigns?.forEach(campaign => {
            flat.push({ result: campaign, type: 'campaign' });
        });
        
        results.placements?.forEach(placement => {
            flat.push({ result: placement, type: 'placement' });
        });
        
        results.creatives?.forEach(creative => {
            flat.push({ result: creative, type: 'creative' });
        });
        
        return flat;
    };

    /**
     * Navigates to the detail page for the given result using React Router.
     * Routes by `item_type`: campaign → /campaignDetails, placement → /placementDetailsTable,
     * creative → /createCreatives. Uses `entity_campaign_id` as the route parameter.
     * @param {Object} result - The search result item
     * @param {'campaign'|'placement'|'creatives'} type - The entity type
     */
    navigateToResult = (result, type) => {
        const { navigate } = this.props;
        
        switch(type) {
            case 'campaign':
                navigate(`/campaignDetails/${result.entity_campaign_id}`);
                break;
            case 'placement':
                navigate(`/placementDetailsTable/${result.entity_campaign_id}`, {
                    state: { campaignObj: result, navigateFromGlobalSearch: true, placement: result.entity_name, placementId: result.entity_id }
                });
                break;
            case 'creative':
                navigate(`/createCreatives/${result.entity_campaign_id}`, {
                    state: { campaignObj: result, navigateFromGlobalSearch: true, creative: result.entity_name, creativeId: result.entity_id, creativeType: result.item_type }
                });
                break;
            default:
                break;
        }
    };

    /**
     * Resets the search input and results back to the initial empty state
     * and returns focus to the search input.
     */
    clearSearch = () => {
        this.setState({
            searchQuery: '',
            results: { campaigns: [], placements: [], creatives: [] },
            isLoading: false,
            selectedIndex: -1,
            totalResults: 0
        });
        if (this.searchInputRef.current) {
            this.searchInputRef.current.focus();
        }
    };

    /**
     * Closes the overlay when the user clicks directly on the backdrop
     * (not on the search container itself).
     * @param {React.MouseEvent} e
     */
    handleBackdropClick = (e) => {
        if (e.target.className.includes('global-search-overlay')) {
            this.props.onClose();
        }
    };

    /**
     * Returns the appropriate Omni icon for a given entity type.
     * @param {'campaign'|'placement'|'creative'} type
     * @returns {JSX.Element}
     */
    renderResultIcon = (type) => {
        const iconMap = {
            campaign: 'omni:object:campaign',
            placement: 'omni:informative:package',
            creative: 'omni:object:creative'
        };
        
        return (
            <OmniIconComponent 
                className="result-icon is-size-4" 
                icon-id={iconMap[type] || 'omni:informative:info'}
            />
        );
    };

    /**
     * Renders the results panel below the search input.
     * Shows a loading indicator, a minimum-length hint, a no-results message,
     * or the grouped results (campaigns, placements, creatives) as appropriate.
     * @returns {JSX.Element|null}
     */
    renderResults = () => {
        const { results, searchQuery, isLoading, selectedIndex, totalResults } = this.state;
        
        // Don't show anything if user hasn't typed yet
        if (searchQuery.length === 0) {
            return null;
        }
        
        if (isLoading) {
            return (
                <div className="global-search-loading">
                    <OmniLoadingIndicatorComponent />
                </div>
            );
        }
        
        if (searchQuery.length < 3) {
            return (
                <div className="no-results">
                    <p>Type at least 4 characters to search</p>
                </div>
            );
        }
        
        if (totalResults === 0) {
            return (
                <div className="no-results">
                    <OmniIconComponent className="is-size-1" icon-id="omni:informative:error" />
                    <p>No results found for "{searchQuery}"</p>
                    <span>Try different keywords or check spelling</span>
                </div>
            );
        }

        let currentIndex = 0;
        
        return (
            <div className="global-search-results">
                {/* Campaigns */}
                {results.campaigns?.length > 0 && (
                    <div className="result-group">
                        <div className="group-title">Campaigns ({results.campaigns.length})</div>
                        {results.campaigns.map((campaign, index) => {
                            const isSelected = currentIndex === selectedIndex;
                            currentIndex++;
                            return (
                                <div
                                    key={`campaign-${index}`}
                                    className={`result-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => this.handleResultClick(campaign, 'campaign')}
                                >
                                    {this.renderResultIcon('campaign')}
                                    <div className="result-content">
                                        <div className="result-title">{campaign.entity_name}</div>
                                        <div className="result-subtitle">
                                            {campaign.created_at}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Placements */}
                {results.placements?.length > 0 && (
                    <div className="result-group">
                        <div className="group-title">Placements ({results.placements.length})</div>
                        {results.placements.map((placement, index) => {
                            const isSelected = currentIndex === selectedIndex;
                            currentIndex++;
                            return (
                                <div
                                    key={`placement-${index}`}
                                    className={`result-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => this.handleResultClick(placement, 'placement')}
                                >
                                    {this.renderResultIcon('placement')}
                                    <div className="result-content">
                                        <div className="result-title">{placement.entity_name}</div>
                                        <div className="result-subtitle">
                                            {placement.created_at}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Creatives */}
                {results.creatives?.length > 0 && (
                    <div className="result-group">
                        <div className="group-title">Creatives ({results.creatives.length})</div>
                        {results.creatives.map((creative, index) => {
                            const isSelected = currentIndex === selectedIndex;
                            currentIndex++;
                            return (
                                <div
                                    key={`creative-${index}`}
                                    className={`result-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => this.handleResultClick(creative, 'creative')}
                                >
                                    {this.renderResultIcon('creative')}
                                    <div className="result-content">
                                        <div className="result-title">{creative.entity_name}</div>
                                        <div className="result-subtitle">
                                            {creative.created_at}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { searchQuery, isLoading } = this.state;
        
        return (
            <div id="global-search-wrapper">
                <div className="global-search-overlay" onClick={this.handleBackdropClick}>
                    <div className="global-search-container">
                    {/* Search Input */}
                    <div className="global-search-input-wrapper">
                        <OmniIconComponent 
                            className="search-icon is-size-4" 
                            icon-id="omni:interactive:search"
                        />
                        <input
                            ref={this.searchInputRef}
                            type="text"
                            className="global-search-input"
                            placeholder="Search campaigns, placements, creatives..."
                            value={searchQuery}
                            onChange={this.handleSearchChange}
                            autoComplete="off"
                        />
                        {searchQuery && (
                            <OmniIconComponent 
                                className="clear-icon is-size-4" 
                                icon-id="omni:interactive:close"
                                onClick={this.clearSearch}
                            />
                        )}
                    </div>

                    {/* Results */}
                    {this.renderResults()}
                </div>
            </div>
            </div>
        );
    }
}
